import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getFirestore,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";

import { firebaseApp } from "../config/firebase";
import ConfirmDelete from "../components/confirmDelete";
import { Loader } from "../hooks/useLoading";
import { toastMessage } from "../utils/toastMessage";

const db = getFirestore(firebaseApp);

const ConnectionReasons = () => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedReason, setSelectedReason] = useState(null);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const [reasonName, setReasonName] = useState("");
  const [sortOrder, setSortOrder] = useState(1);
  const [reasonStatus, setReasonStatus] = useState(true);

  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /*
   * Get connection reasons from Firebase
   */
  const [snapshot, loading, error] = useCollection(
    collection(db, "connection_reasons"),
    {
      snapshotListenOptions: {
        includeMetadataChanges: true,
      },
    },
  );

  /*
   * Convert Firestore timestamp to milliseconds
   */
  const toMillis = (value) => {
    if (!value) return 0;

    if (typeof value === "object" && typeof value.seconds === "number") {
      return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value === "number") {
      return value;
    }

    const timestamp = Date.parse(String(value));

    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  /*
   * Convert Firestore documents into array
   */
  const reasons = useMemo(() => {
    if (!snapshot?.docs) {
      return [];
    }

    return snapshot.docs
      .map((item) => ({
        id: item.id,
        ...item.data(),
      }))
      .sort((a, b) => {
        const aOrder = Number(a.sort_order || 999999);
        const bOrder = Number(b.sort_order || 999999);

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        return toMillis(b.created_at) - toMillis(a.created_at);
      });
  }, [snapshot]);

  /*
   * Search
   */
  const filteredReasons = useMemo(() => {
    const searchValue = String(search || "")
      .trim()
      .toLowerCase();

    if (!searchValue) {
      return reasons;
    }

    return reasons.filter((item) =>
      String(item.name || "")
        .toLowerCase()
        .includes(searchValue),
    );
  }, [reasons, search]);

  /*
   * Pagination
   */
  const totalPages = Math.max(
    1,
    Math.ceil(filteredReasons.length / rowsPerPage),
  );

  const paginatedReasons = filteredReasons.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, rowsPerPage]);

  /*
   * Reset form
   */
  const resetForm = () => {
    setSelectedReason(null);
    setReasonName("");
    setSortOrder(1);
    setReasonStatus(true);
  };

  /*
   * Open Add modal
   */
  const handleAdd = () => {
    resetForm();

    const maxSortOrder = reasons.reduce(
      (max, item) => Math.max(max, Number(item.sort_order || 0)),
      0,
    );

    setSortOrder(maxSortOrder + 1);

    setShowModal(true);
  };

  /*
   * Open Edit modal
   */
  const handleEdit = (reason) => {
    setSelectedReason(reason);
    setReasonName(reason.name || "");
    setSortOrder(Number(reason.sort_order || 1));
    setReasonStatus(reason.status !== false);

    setShowModal(true);
  };

  /*
   * Close modal
   */
  const handleCloseModal = () => {
    if (saving) {
      return;
    }

    setShowModal(false);
    resetForm();
  };

  /*
   * Check duplicate reason
   */
  const isDuplicateReason = () => {
    const normalizedName = reasonName.trim().toLowerCase();

    return reasons.some((item) => {
      if (selectedReason && item.id === selectedReason.id) {
        return false;
      }

      return (
        String(item.name || "")
          .trim()
          .toLowerCase() === normalizedName
      );
    });
  };

  /*
   * Add / Update reason
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = reasonName.trim();

    if (!name) {
      toastMessage("Please enter connection reason.", "error");
      return;
    }

    if (isDuplicateReason()) {
      toastMessage("This connection reason already exists.", "error");
      return;
    }

    if (!sortOrder || Number(sortOrder) < 1) {
      toastMessage("Sort order must be greater than 0.", "error");
      return;
    }

    try {
      setSaving(true);

      const data = {
        name,
        status: Boolean(reasonStatus),
        sort_order: Number(sortOrder),
      };

      /*
       * UPDATE
       */
      if (selectedReason) {
        const reasonRef = doc(db, "connection_reasons", selectedReason.id);

        await updateDoc(reasonRef, {
          ...data,
          updated_at: serverTimestamp(),
        });

        toastMessage("Connection reason updated successfully.", "success");
      } else {

      /*
       * CREATE
       */
        await addDoc(collection(db, "connection_reasons"), {
          ...data,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });

        toastMessage("Connection reason added successfully.", "success");
      }

      handleCloseModal();
    } catch (error) {
      console.error("Connection reason save error:", error);

      toastMessage("Failed to save connection reason.", "error");
    } finally {
      setSaving(false);
    }
  };

  /*
   * Activate / Deactivate
   */
  const handleToggleStatus = async (reason) => {
    try {
      const reasonRef = doc(db, "connection_reasons", reason.id);

      const newStatus = reason.status === false;

      await updateDoc(reasonRef, {
        status: newStatus,
        updated_at: serverTimestamp(),
      });

      toastMessage(
        newStatus
          ? "Connection reason activated successfully."
          : "Connection reason deactivated successfully.",
        "success",
      );
    } catch (error) {
      console.error("Connection reason status error:", error);

      toastMessage("Failed to update connection reason status.", "error");
    }
  };

  /*
   * Delete reason
   */
  const handleDelete = async (id) => {
    try {
      setDeleting(true);

      const reasonRef = doc(db, "connection_reasons", id);

      await deleteDoc(reasonRef);

      toastMessage("Connection reason deleted successfully.", "success");

      setShowDeleteModal(false);
      setSelectedDeleteId(null);
    } catch (error) {
      console.error("Connection reason delete error:", error);

      toastMessage("Failed to delete connection reason.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const isLoading = loading || saving || deleting;

  if (error) {
    console.error("Connection reasons Firestore error:", error);
  }

  return (
    <>
      {isLoading && <Loader />}

      {/* Header */}
      <header className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100">
        <div>
          <h4 className="mb-1">Connection Reasons</h4>

          <small className="text-muted">
            Manage the reasons users can select when sending a connection
            request.
          </small>
        </div>

        <Button variant="primary" onClick={handleAdd} disabled={isLoading}>
          Add Connection Reason
        </Button>
      </header>

      {/* Search */}
      <div className="d-flex justify-content-between align-items-end gap-3 mb-3">
        <div className="d-flex flex-column">
          <label className="mb-1">Search Reason:</label>

          <input
            type="text"
            className="form-control"
            style={{ minWidth: "300px" }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search connection reason"
          />
        </div>

        <span className="badge text-bg-secondary px-3 py-2">
          Total: {filteredReasons.length}
        </span>
      </div>

      {/* Table */}
      {paginatedReasons.length > 0 ? (
        <div className="table-responsive">
          <table className="table">
            <thead className="table-secondary">
              <tr>
                <th style={{ width: "70px" }}>#</th>

                <th>Connection Reason</th>

                <th
                  style={{
                    width: "120px",
                    textAlign: "center",
                  }}
                >
                  Sort Order
                </th>

                <th
                  style={{
                    width: "120px",
                    textAlign: "center",
                  }}
                >
                  Status
                </th>

                <th
                  style={{
                    width: "300px",
                    textAlign: "center",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedReasons.map((reason, index) => (
                <tr key={reason.id}>
                  <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>

                  <td>
                    <strong>{reason.name}</strong>
                  </td>

                  <td className="text-center">{reason.sort_order}</td>

                  <td className="text-center">
                    {reason.status !== false ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Inactive</span>
                    )}
                  </td>

                  <td className="text-center">
                    <div className="d-flex justify-content-center gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(reason)}
                        disabled={isLoading}
                      >
                        Edit
                      </button>

                      <button
                        className={`btn btn-sm ${
                          reason.status !== false
                            ? "btn-warning"
                            : "btn-success"
                        }`}
                        onClick={() => handleToggleStatus(reason)}
                        disabled={isLoading}
                      >
                        {reason.status !== false ? "Deactivate" : "Activate"}
                      </button>

                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          setSelectedDeleteId(reason.id);
                          setShowDeleteModal(true);
                        }}
                        disabled={isLoading}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !isLoading && (
          <div className="text-center py-5 border rounded">
            <h5>No Connection Reasons Found</h5>

            <p className="text-muted mb-3">
              Add a connection reason to make it available in the mobile app.
            </p>

            <Button variant="primary" onClick={handleAdd}>
              Add Connection Reason
            </Button>
          </div>
        )
      )}

      {/* Pagination */}
      {!isLoading && filteredReasons.length > 0 && (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3 p-3 border rounded bg-light">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge text-bg-secondary px-3 py-2">
              Total: {filteredReasons.length}
            </span>

            <label htmlFor="reasons-per-page" className="mb-0 fw-semibold">
              Per page:
            </label>

            <select
              id="reasons-per-page"
              className="form-control form-control-sm"
              style={{ width: "90px" }}
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>

              <option value={25}>25</option>

              <option value={50}>50</option>

              <option value={100}>100</option>
            </select>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="text-muted small">
              Showing{" "}
              {filteredReasons.length === 0
                ? 0
                : (currentPage - 1) * rowsPerPage + 1}{" "}
              - {Math.min(currentPage * rowsPerPage, filteredReasons.length)} of{" "}
              {filteredReasons.length}
            </span>

            <div className="btn-group" role="group">
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </button>

              <button className="btn btn-primary btn-sm" disabled>
                Page {currentPage} / {totalPages}
              </button>

              <button
                className="btn btn-outline-primary btn-sm"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedReason
                ? "Edit Connection Reason"
                : "Add Connection Reason"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                Connection Reason <span className="text-danger">*</span>
              </Form.Label>

              <Form.Control
                type="text"
                value={reasonName}
                onChange={(e) => setReasonName(e.target.value)}
                placeholder="Enter connection reason"
                maxLength={150}
                disabled={saving}
              />

              <Form.Text className="text-muted">
                This reason will be displayed in the mobile app.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Sort Order <span className="text-danger">*</span>
              </Form.Label>

              <Form.Control
                type="number"
                min="1"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>

              <Form.Select
                value={reasonStatus ? "active" : "inactive"}
                onChange={(e) => setReasonStatus(e.target.value === "active")}
                disabled={saving}
              >
                <option value="active">Active</option>

                <option value="inactive">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              type="submit"
              disabled={saving || !reasonName.trim()}
            >
              {saving
                ? "Saving..."
                : selectedReason
                  ? "Update Reason"
                  : "Add Reason"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      {showDeleteModal && (
        <ConfirmDelete
          confirmFunction={() => handleDelete(selectedDeleteId)}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedDeleteId(null);
          }}
        />
      )}
    </>
  );
};

export default ConnectionReasons;
